import React, { useState } from 'react';
import { useSEO } from '../lib/useSEO';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Check, X, Zap, Crown, Sparkles, ArrowRight, Shield,
  BarChart3, Brain, Target, Layers, Calendar, Star, Flame
} from 'lucide-react';
import { useAuthStore } from '../lib/auth';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png';

const FREE_FEATURES = [
  { text: 'Daily Challenge', included: true },
  { text: 'Full Leaderboards', included: true },
  { text: '1x25 Clue Sprint', included: true },
  { text: 'Unlimited Sprints', included: false },
  { text: 'Unlimited Daily Categories', included: false },
  { text: 'Unlimited Play a Board', included: false },
  { text: 'Data Analytics', included: false },
  { text: 'AskAlex AI', included: false },
  { text: 'Study Path', included: false },
];

const PAID_FEATURES = [
  { text: 'Daily Challenge', included: true },
  { text: 'Full Leaderboards', included: true },
  { text: 'Unlimited Sprints', included: true },
  { text: 'Unlimited Daily Categories', included: true },
  { text: 'Unlimited Play a Board', included: true },
  { text: 'Data Analytics', included: true },
  { text: 'AskAlex AI', included: true },
  { text: 'Beat the Score', included: true },
  { text: 'Study Path', included: true },
];

const BILLING_CYCLES = [
  { id: 'monthly', label: 'Monthly', price: 14, period: '/mo', savings: null },
  { id: 'quarterly', label: 'Quarterly', price: 29, period: '/3 mo', savings: '31%', monthly: 9.67, daily: 0.32 },
  { id: 'yearly', label: 'Yearly', price: 89, period: '/yr', savings: '47%', monthly: 7.42, daily: 0.24 },
];

export default function Pricing() {
  useSEO('Pricing | JepPrep', 'Choose the plan that fits your trivia training goals. Start free or unlock unlimited features with JepPrep Pro.', { canonical: 'https://jepprep.com/pricing' });
  const [selectedCycle, setSelectedCycle] = useState('monthly');
  const { user } = useAuthStore();

  const activeCycle = BILLING_CYCLES.find(c => c.id === selectedCycle);

  // Inject Product JSON-LD for pricing
  React.useEffect(() => {
    const pricingJsonLd = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "JepPrep Pro",
      "applicationCategory": "EducationalApplication",
      "operatingSystem": "Web",
      "description": "Unlimited Jeopardy-style trivia training with AI-generated boards, study paths, analytics, and daily challenges.",
      "url": "https://jepprep.com/pricing",
      "offers": [
        {
          "@type": "Offer",
          "name": "Free Plan",
          "price": "0",
          "priceCurrency": "USD",
          "description": "Daily Challenge, leaderboards, and 1 sprint per day"
        },
        {
          "@type": "Offer",
          "name": "JepPrep Pro Monthly",
          "price": "14",
          "priceCurrency": "USD",
          "billingIncrement": 1,
          "unitCode": "MON",
          "description": "Unlimited access to all features, billed monthly"
        },
        {
          "@type": "Offer",
          "name": "JepPrep Pro Quarterly",
          "price": "29",
          "priceCurrency": "USD",
          "description": "Unlimited access, billed quarterly — save 31%"
        },
        {
          "@type": "Offer",
          "name": "JepPrep Pro Yearly",
          "price": "89",
          "priceCurrency": "USD",
          "description": "Unlimited access, billed yearly — save 47%"
        }
      ]
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-pricing-schema', 'true');
    script.textContent = JSON.stringify(pricingJsonLd);
    document.head.appendChild(script);
    return () => { if (script.parentNode) script.parentNode.removeChild(script); };
  }, []);

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="pricing-page">
      {/* Header */}
      <header className="border-b border-[hsl(var(--border))]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="pricing-logo">
            <img src={LOGO_URL} alt="JepPrep" className="h-8 w-auto" />
          </Link>
          <div className="flex items-center gap-3">
            {user ? (
              <Link to="/dashboard">
                <Button className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-gold-400))] font-semibold" data-testid="pricing-dashboard-btn">
                  Dashboard
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="text-gray-400 hover:text-white" data-testid="pricing-login-btn">Sign In</Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-gold-400))] font-semibold" data-testid="pricing-signup-btn">
                    Start Free
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6">
        {/* Hero */}
        <div className="text-center pt-16 pb-12">
          <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/30 mb-6" data-testid="prelaunch-banner">
            <Sparkles className="w-4 h-4 text-emerald-400" />
            <span className="text-sm text-emerald-400 font-semibold tracking-wide">Pre-Launch Free Unlimited Access until May 31, 2026</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight" data-testid="pricing-heading">
            Invest in your<br />
            <span className="text-[hsl(var(--brand-gold-500))]">trivia mastery</span>
          </h1>
          <p className="text-base sm:text-lg text-gray-400 max-w-2xl mx-auto leading-relaxed">
            Start with our free tier and upgrade when you're ready to unlock the full JepPrep training arsenal.
          </p>
        </div>

        {/* Billing Toggle — Desktop Only */}
        <div className="hidden md:flex justify-center mb-12 px-4">
          <div className="inline-flex items-center p-1 rounded-xl bg-[hsl(var(--secondary))] border border-[hsl(var(--border))] overflow-x-auto no-scrollbar" data-testid="billing-toggle">
            {BILLING_CYCLES.map(cycle => (
              <button
                key={cycle.id}
                onClick={() => setSelectedCycle(cycle.id)}
                className={`relative whitespace-nowrap px-5 py-2 rounded-lg text-sm font-medium transition-all ${
                  selectedCycle === cycle.id
                    ? 'bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] shadow-lg shadow-[hsl(var(--brand-gold-500))]/20'
                    : 'text-gray-400 hover:text-white'
                }`}
                data-testid={`billing-${cycle.id}`}
              >
                {cycle.label}
                {cycle.savings && (
                  <span className={`ml-1.5 text-xs font-bold ${
                    selectedCycle === cycle.id ? 'text-[hsl(var(--brand-blue-900))]/70' : 'text-emerald-400'
                  }`}>
                    -{cycle.savings}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Mobile Swipeable Plans */}
        <div className="md:hidden mb-12 -mx-6">
          <div className="flex overflow-x-auto snap-x snap-mandatory gap-4 px-6 pb-4 no-scrollbar" data-testid="mobile-plan-carousel">
            {/* Free Plan Card */}
            <div className="snap-center shrink-0 w-[85vw] max-w-[320px] rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--secondary))] p-6 flex flex-col">
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-9 h-9 rounded-xl bg-[hsl(var(--accent))] flex items-center justify-center">
                    <Zap className="w-4 h-4 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white">Free</h3>
                </div>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-4xl font-bold text-white">$0</span>
                  <span className="text-gray-500 text-sm">/forever</span>
                </div>
                <p className="text-xs text-gray-500">Get a taste of game show training</p>
              </div>
              <Link to="/register" className="block mb-5">
                <Button variant="outline" className="w-full py-4 text-sm border-[hsl(var(--border))] text-white hover:bg-[hsl(var(--accent))]">
                  Get Started <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <div className="space-y-2.5 flex-1">
                {FREE_FEATURES.slice(0, 6).map(feat => (
                  <div key={feat.text} className="flex items-center gap-2.5">
                    {feat.included ? (
                      <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                    ) : (
                      <X className="w-3.5 h-3.5 text-gray-600 shrink-0" />
                    )}
                    <span className={feat.included ? 'text-xs text-gray-300' : 'text-xs text-gray-600'}>{feat.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Paid Plan Cards */}
            {BILLING_CYCLES.map((cycle) => (
              <div
                key={cycle.id}
                className={`snap-center shrink-0 w-[85vw] max-w-[320px] rounded-2xl border-2 p-6 flex flex-col relative overflow-hidden ${
                  cycle.id === 'yearly'
                    ? 'border-[hsl(var(--brand-gold-500))]/50 bg-[hsl(var(--secondary))]'
                    : 'border-[hsl(var(--border))] bg-[hsl(var(--secondary))]'
                }`}
              >
                {cycle.id === 'yearly' && (
                  <div className="absolute top-4 right-4">
                    <div className="flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-[hsl(var(--brand-gold-500))]/15 border border-[hsl(var(--brand-gold-500))]/30">
                      <Crown className="w-3 h-3 text-[hsl(var(--brand-gold-500))]" />
                      <span className="text-xs font-semibold text-[hsl(var(--brand-gold-500))]">Best Value</span>
                    </div>
                  </div>
                )}
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-9 h-9 rounded-xl bg-[hsl(var(--brand-gold-500))]/15 flex items-center justify-center">
                      <Crown className="w-4 h-4 text-[hsl(var(--brand-gold-500))]" />
                    </div>
                    <h3 className="text-lg font-bold text-white">Pro {cycle.label}</h3>
                  </div>
                  <div className="flex items-baseline gap-1 mb-1">
                    <span className="text-4xl font-bold text-white">${cycle.price}</span>
                    <span className="text-gray-500 text-sm">{cycle.period}</span>
                  </div>
                  {cycle.monthly ? (
                    <p className="text-xs text-emerald-400 font-medium">
                      ${cycle.monthly.toFixed(2)}/mo — save {cycle.savings}
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500">Full access to everything</p>
                  )}
                </div>
                <Link to="/register" className="block mb-5">
                  <Button className="w-full py-4 text-sm bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-gold-400))] font-bold">
                    Upgrade to Pro <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <div className="space-y-2.5 flex-1">
                  {PAID_FEATURES.slice(0, 6).map(feat => (
                    <div key={feat.text} className="flex items-center gap-2.5">
                      <Check className="w-3.5 h-3.5 text-[hsl(var(--brand-gold-500))] shrink-0" />
                      <span className="text-xs text-gray-200">{feat.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {/* Swipe hint */}
          <p className="text-center text-xs text-gray-600 mt-2">Swipe to see all plans</p>
        </div>

        {/* Desktop Pricing Cards */}
        <div className="hidden md:grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-20">
          {/* Free Tier */}
          <div
            className="relative rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--secondary))] p-8 flex flex-col"
            data-testid="pricing-card-free"
          >
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-10 h-10 rounded-xl bg-[hsl(var(--accent))] flex items-center justify-center">
                  <Zap className="w-5 h-5 text-gray-400" />
                </div>
                <h3 className="text-xl font-bold text-white">Free</h3>
              </div>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-5xl font-bold text-white tracking-tight">$0</span>
                <span className="text-gray-500 text-sm">/forever</span>
              </div>
              <p className="text-sm text-gray-500">Get a taste of game show training</p>
            </div>

            <Link to="/register" className="block mb-8" data-testid="free-cta">
              <Button variant="outline" className="w-full py-5 text-base border-[hsl(var(--border))] text-white hover:bg-[hsl(var(--accent))] hover:border-[hsl(var(--brand-gold-500))]/40 transition-all">
                Get Started <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>

            <div className="space-y-3 flex-1">
              <p className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-4">What's included</p>
              {FREE_FEATURES.map(feat => (
                <div key={feat.text} className="flex items-center gap-3">
                  {feat.included ? (
                    <div className="w-5 h-5 rounded-full bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-emerald-400" />
                    </div>
                  ) : (
                    <div className="w-5 h-5 rounded-full bg-[hsl(var(--accent))] flex items-center justify-center flex-shrink-0">
                      <X className="w-3 h-3 text-gray-600" />
                    </div>
                  )}
                  <span className={feat.included ? 'text-sm text-gray-300' : 'text-sm text-gray-600'}>{feat.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Pro Tier */}
          <div
            className="relative rounded-2xl border-2 border-[hsl(var(--brand-gold-500))]/50 bg-[hsl(var(--secondary))] p-8 flex flex-col overflow-hidden"
            data-testid="pricing-card-pro"
          >
            {/* Glow effect */}
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-[hsl(var(--brand-gold-500))]/8 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-16 -left-16 w-32 h-32 bg-[hsl(var(--brand-gold-500))]/5 rounded-full blur-3xl pointer-events-none" />

            {/* Popular badge */}
            <div className="absolute top-5 right-5">
              <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-[hsl(var(--brand-gold-500))]/15 border border-[hsl(var(--brand-gold-500))]/30">
                <Crown className="w-3.5 h-3.5 text-[hsl(var(--brand-gold-500))]" />
                <span className="text-xs font-semibold text-[hsl(var(--brand-gold-500))]">Most Popular</span>
              </div>
            </div>

            <div className="mb-6 relative">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-10 h-10 rounded-xl bg-[hsl(var(--brand-gold-500))]/15 flex items-center justify-center">
                  <Crown className="w-5 h-5 text-[hsl(var(--brand-gold-500))]" />
                </div>
                <h3 className="text-xl font-bold text-white">Pro</h3>
              </div>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-5xl font-bold text-white tracking-tight">${activeCycle.price}</span>
                <span className="text-gray-500 text-sm">{activeCycle.period}</span>
              </div>
              {activeCycle.monthly && (
                <div className="mb-1">
                  <p className="text-sm text-emerald-400 font-medium">
                    Just ${activeCycle.monthly.toFixed(2)}/mo — save {activeCycle.savings}
                  </p>
                  {activeCycle.daily && (
                    <p className="text-xs text-gray-500 mt-0.5">
                      That's only ${activeCycle.daily.toFixed(2)}/day
                    </p>
                  )}
                </div>
              )}
              {!activeCycle.monthly && (
                <p className="text-sm text-gray-500">Full access to everything</p>
              )}
            </div>

            <Link to="/register" className="block mb-8 relative" data-testid="pro-cta">
              <Button className="w-full py-5 text-base bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-gold-400))] font-bold shadow-lg shadow-[hsl(var(--brand-gold-500))]/20 transition-all">
                Upgrade to Pro <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>

            <div className="space-y-3 flex-1 relative">
              <p className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-4">Everything in Free, plus</p>
              {PAID_FEATURES.map(feat => (
                <div key={feat.text} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-[hsl(var(--brand-gold-500))]/15 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-[hsl(var(--brand-gold-500))]" />
                  </div>
                  <span className="text-sm text-gray-200">{feat.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Feature Breakdown */}
        <div className="max-w-4xl mx-auto mb-20">
          <h2 className="text-2xl font-bold text-white text-center mb-10">What Pro unlocks for your training</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: Target, title: 'Unlimited Sprints', desc: 'Timed rounds with 25, 50, or 100 clues. No daily limits.' },
              { icon: Layers, title: 'Unlimited Play a Board', desc: 'AI-generated game boards with 3 rounds.' },
              { icon: BarChart3, title: 'Data Analytics', desc: 'Deep category breakdowns, streaks, and performance trends.' },
              { icon: Brain, title: 'AskAlex AI', desc: 'AI missions, Beat the Data challenges, and Knowledge Portfolio.' },
              { icon: Calendar, title: 'Unlimited Daily Categories', desc: 'Access every category including themed and seasonal sets.' },
              { icon: Flame, title: 'Study Path', desc: 'Personalized learning that adapts to your weak categories.' },
            ].map(feat => (
              <div
                key={feat.title}
                className="group p-5 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--secondary))] hover:border-[hsl(var(--brand-gold-500))]/30 transition-all"
              >
                <div className="w-9 h-9 rounded-lg bg-[hsl(var(--brand-gold-500))]/10 flex items-center justify-center mb-3 group-hover:bg-[hsl(var(--brand-gold-500))]/20 transition-colors">
                  <feat.icon className="w-4.5 h-4.5 text-[hsl(var(--brand-gold-500))]" />
                </div>
                <h3 className="text-base font-semibold text-white mb-1.5">{feat.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Comparison Table */}
        <div className="max-w-3xl mx-auto mb-20">
          <h2 className="text-2xl font-bold text-white text-center mb-8">Plan comparison</h2>
          <div className="rounded-2xl border border-[hsl(var(--border))] overflow-hidden" data-testid="pricing-comparison-table">
            <table className="w-full">
              <thead>
                <tr className="bg-[hsl(var(--accent))]">
                  <th className="text-left text-xs sm:text-sm font-medium text-gray-400 px-3 sm:px-6 py-3 sm:py-4">Feature</th>
                  <th className="text-center text-xs sm:text-sm font-medium text-gray-400 px-2 sm:px-6 py-3 sm:py-4 w-16 sm:w-28">Free</th>
                  <th className="text-center text-xs sm:text-sm font-medium px-2 sm:px-6 py-3 sm:py-4 w-16 sm:w-28">
                    <span className="text-[hsl(var(--brand-gold-500))]">Pro</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Daily Challenge', true, true],
                  ['Full Leaderboards', true, true],
                  ['Sprint Mode', '1x25', 'Unlimited'],
                  ['Daily Categories', 'Limited', 'Unlimited'],
                  ['Play a Board', false, 'Unlimited'],
                  ['Data Analytics', false, true],
                  ['AskAlex AI', false, true],
                  ['Beat the Score', false, true],
                  ['Study Path', false, true],
                  ['Priority Support', false, true],
                ].map(([feature, free, pro], idx) => (
                  <tr key={feature} className={idx % 2 === 0 ? 'bg-[hsl(var(--secondary))]' : 'bg-[hsl(var(--secondary))]/50'}>
                    <td className="text-xs sm:text-sm text-gray-300 px-3 sm:px-6 py-3 sm:py-3.5">{feature}</td>
                    <td className="text-center px-2 sm:px-6 py-3 sm:py-3.5">
                      {free === true ? <Check className="w-4 h-4 text-emerald-400 mx-auto" /> :
                       free === false ? <X className="w-4 h-4 text-gray-600 mx-auto" /> :
                       <span className="text-xs text-gray-500">{free}</span>}
                    </td>
                    <td className="text-center px-2 sm:px-6 py-3 sm:py-3.5">
                      {pro === true ? <Check className="w-4 h-4 text-[hsl(var(--brand-gold-500))] mx-auto" /> :
                       pro === false ? <X className="w-4 h-4 text-gray-600 mx-auto" /> :
                       <span className="text-xs text-[hsl(var(--brand-gold-500))] font-medium">{pro}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* FAQ */}
        <div className="max-w-2xl mx-auto mb-20">
          <h2 className="text-2xl font-bold text-white text-center mb-8">Frequently asked questions</h2>
          <div className="space-y-4">
            {[
              {
                q: 'Can I try JepPrep before committing?',
                a: 'Absolutely. The Free tier gives you full access to the Daily Challenge, Daily Leaderboard, and a 25-clue Sprint every day. No credit card required.'
              },
              {
                q: 'Can I change plans later?',
                a: 'Yes. Upgrade, downgrade, or cancel anytime. Please note that we do not prorate or refund for cancellations or downgrades — your current plan remains active until the end of your billing period.'
              },
              {
                q: 'What happens to my data if I cancel Pro?',
                a: 'Your account and all your stats stay intact. You\'ll still have access to the Free tier features. If you re-subscribe, everything picks up right where you left off.'
              },
              {
                q: 'Is the yearly plan really 47% off?',
                a: 'Yes. The monthly plan is $14/mo ($168/yr). The yearly plan is $89/yr — that\'s $7.42/mo, saving you $79 per year.'
              },
            ].map((faq, i) => (
              <details key={i} className="group rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--secondary))]" data-testid={`faq-${i}`}>
                <summary className="cursor-pointer px-6 py-4 text-sm font-medium text-white list-none flex items-center justify-between">
                  {faq.q}
                  <span className="text-gray-500 group-open:rotate-45 transition-transform text-lg">+</span>
                </summary>
                <div className="px-6 pb-4 text-sm text-gray-400 leading-relaxed">{faq.a}</div>
              </details>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center pb-20">
          <div className="max-w-xl mx-auto">
            <div className="flex items-center justify-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 text-[hsl(var(--brand-gold-500))] fill-[hsl(var(--brand-gold-500))]" />)}
            </div>
            <p className="text-gray-400 text-sm mb-6 italic">"JepPrep made me feel like I was actually training for the show. The boards are incredibly realistic."</p>
            <Link to="/register" data-testid="pricing-bottom-cta">
              <Button size="lg" className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-gold-400))] font-bold text-base px-8 py-6 shadow-lg shadow-[hsl(var(--brand-gold-500))]/20">
                Start Training Free <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <p className="text-xs text-gray-600 mt-3">No credit card required</p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[hsl(var(--border))] py-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-gray-600">&copy; {new Date().getFullYear()} JepPrep. All rights reserved.</p>
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-600">
            <Shield className="w-3.5 h-3.5" />
            <span>Secure payments</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
